import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signup, googleAuth, applyReferralCode } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useTutorialVideos } from '../contexts/TutorialVideoContext';
import GoogleAuthButton from './GoogleAuthButton';
import AuthLayout from './AuthLayout';

function SignupForm({ onSignup }) {
  const { refreshProfile } = useUser();
  const { refreshVideos } = useTutorialVideos();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-fill referral code from URL query param
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Also save to sessionStorage for Google OAuth flow
      sessionStorage.setItem('pendingReferralCode', ref);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear previous errors

    // Validate inputs
    if (!username || !email || !password) {
      setError('Please enter username, email, and password');
      return;
    }

    // Basic email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup(username, password, email);
      setIsLoading(false);

      if (result.token) {
        // Apply referral code if provided (don't block signup on failure)
        const codeToApply = referralCode.trim() || sessionStorage.getItem('pendingReferralCode');
        if (codeToApply) {
          try {
            await applyReferralCode(codeToApply);
          } catch (refErr) {
            console.warn('Referral code application failed:', refErr);
          }
          sessionStorage.removeItem('pendingReferralCode');
        }

        await refreshProfile(); // Fetch user profile for navbar
        refreshVideos(); // Fetch tutorial videos for walkthrough buttons
        onSignup();
        navigate('/watchlist'); // redirect
      } else {
        const errorMsg = result.error || 'Signup failed. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      setIsLoading(false);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  const handleGoogleSuccess = async (credential) => {
    setError('');
    setIsLoading(true);

    const result = await googleAuth(credential);
    setIsLoading(false);

    if (result.token) {
      // Apply referral code if saved before Google redirect
      const codeToApply = sessionStorage.getItem('pendingReferralCode');
      if (codeToApply) {
        try {
          await applyReferralCode(codeToApply);
        } catch (refErr) {
          console.warn('Referral code application failed:', refErr);
        }
        sessionStorage.removeItem('pendingReferralCode');
      }

      await refreshProfile(); // Fetch user profile for navbar
      refreshVideos(); // Fetch tutorial videos for walkthrough buttons
      onSignup(); // update App state
      navigate('/watchlist'); // redirect
    } else {
      setError(result.error || 'Google authentication failed. Please try again.');
    }
  };

  const handleGoogleError = (error) => {
    setError(error || 'Google authentication failed. Please try again.');
  };

  return (
    <AuthLayout>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-surface rounded-lg shadow-lg p-6 space-y-5 border border-muted"
      >
        <h2 className="text-2xl font-bold text-brand-white text-center">Create Account</h2>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        <input
          type="text"
          placeholder="Choose a username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <input
          type="password"
          placeholder="Create a password (min 6 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <input
          type="text"
          placeholder="Referral code (optional)"
          value={referralCode}
          onChange={(e) => setReferralCode(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
        />

        <p className="text-center text-brand-gray-blue text-xs">
          By creating an account, you agree to our{' '}
          <a href="/terms" className="text-brand-blue hover:text-[#1a7aef] underline transition-colors">
            Terms of Use
          </a>
          {' '}and{' '}
          <a href="/privacy" className="text-brand-blue hover:text-[#1a7aef] underline transition-colors">
            Privacy Policy
          </a>
        </p>

        <button
          type="submit"
          className="w-full bg-brand-blue text-white font-semibold py-3 rounded-lg hover:bg-[#1a7aef] transition-colors disabled:bg-brand-blue/50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Sign Up'}
        </button>

        <div className="relative flex items-center justify-center my-4">
          <div className="border-t border-muted w-full"></div>
          <span className="px-3 text-sm text-brand-gray-blue bg-surface absolute">or</span>
        </div>

        <GoogleAuthButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
        />

        <div className="border-t border-muted pt-4 mt-4">
          <p className="text-center text-brand-gray-blue text-sm mb-3">
            Already have an account?
          </p>
          <a
            href="/login"
            className="block w-full text-center bg-green-400 text-black font-semibold py-3 rounded-lg hover:bg-green-500 transition-colors"
          >
            Login to Existing Account
          </a>
        </div>
      </form>
    </AuthLayout>
  );
}

export default SignupForm;
